import React, { useMemo, useState } from 'react';
import { formatBrier, formatDate, formatPercent } from './format.js';

// Sort keys are plain accessors so every column sorts on its underlying value rather than the
// formatted string — dates by ISO, Brier by number, and "—" doesn't sort between 0.1 and 0.2.
const COLUMNS = [
  { key: 'name', label: 'Set', align: 'left', value: s => s.name.toLowerCase() },
  { key: 'written', label: 'Written', align: 'left', value: s => s.predictionsMadeOn },
  { key: 'graded-on', label: 'Graded', align: 'left', value: s => s.evaluatedOn },
  { key: 'total', label: 'Predictions', align: 'right', value: s => s.stats.total },
  { key: 'scored', label: 'Graded', align: 'right', value: s => s.stats.scored },
  { key: 'happened', label: 'Happened', align: 'right', value: s => s.stats.yesRate },
  { key: 'brier', label: 'Brier', align: 'right', value: s => s.stats.brier }
];

const DEFAULT_SORT = { key: 'written', direction: 'desc' };

function compare(a, b) {
  // Missing values sort last regardless of direction: an ungraded set has no Brier, and floating
  // it to the top of an ascending sort would just be noise.
  if (a === null || a === undefined || a === '') return 1;
  if (b === null || b === undefined || b === '') return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

// Rows navigate on click, but the real anchor in the Set cell stays — it keeps keyboard focus,
// the status-bar URL preview, and "open in new tab" working. So this deliberately stands aside
// when the click was already on a link, when the user was selecting text, or when a modifier
// asked for a new tab.
function rowNavigation(href) {
  function open(newTab) {
    if (newTab) window.open(href, '_blank', 'noopener');
    else window.location.href = href;
  }

  return {
    onClick: event => {
      if (event.target.closest('a')) return;
      const selection = window.getSelection();
      if (selection && selection.toString()) return;
      open(event.metaKey || event.ctrlKey || event.shiftKey);
    },
    // Middle click, the other "open in a new tab" gesture.
    onAuxClick: event => {
      if (event.button !== 1 || event.target.closest('a')) return;
      event.preventDefault();
      open(true);
    }
  };
}

export default function SetTable({ sets }) {
  const [sort, setSort] = useState(DEFAULT_SORT);

  const sorted = useMemo(() => {
    const column = COLUMNS.find(c => c.key === sort.key) || COLUMNS[0];
    const rows = [...sets].sort((a, b) => compare(column.value(a), column.value(b)));
    // Nulls stay pinned to the bottom, so reverse only the rows that have a value.
    if (sort.direction === 'desc') {
      const missing = rows.filter(s => {
        const v = column.value(s);
        return v === null || v === undefined || v === '';
      });
      const present = rows.filter(s => !missing.includes(s));
      return [...present.reverse(), ...missing];
    }
    return rows;
  }, [sets, sort]);

  function toggle(key) {
    setSort(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'name' ? 'asc' : 'desc' }
    );
  }

  return (
    <div className="pred-table-scroll">
      <table className="pred-set-table">
        <thead>
          <tr>
            {COLUMNS.map(column => {
              const active = sort.key === column.key;
              return (
                <th
                  key={column.key}
                  className={`is-${column.align} ${active ? 'is-sorted' : ''}`}
                  aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <button type="button" onClick={() => toggle(column.key)}>
                    {column.label}
                    <span className="pred-sort-caret" aria-hidden="true">
                      {active ? (sort.direction === 'asc' ? '▲' : '▼') : ''}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map(set => {
            const href = `/set.html?slug=${encodeURIComponent(set.slug)}`;
            return (
              <tr key={set.slug} className="pred-row-link" {...rowNavigation(href)}>
                <td className="is-left">
                  <a className="pred-set-link" href={href}>
                    {set.name}
                  </a>
                  {set.isPrivate && <span className="pred-badge pred-badge-private">Private</span>}
                  {set.description && <div className="pred-set-desc">{set.description}</div>}
                </td>
                <td className="is-left pred-nowrap">{formatDate(set.predictionsMadeOn) || '—'}</td>
                <td className="is-left pred-nowrap">{formatDate(set.evaluatedOn) || '—'}</td>
                <td className="is-right pred-num">{set.stats.total}</td>
                <td className="is-right pred-num">{set.stats.scored}</td>
                <td className="is-right pred-num">
                  {set.stats.scored ? formatPercent(set.stats.yesRate) : '—'}
                </td>
                <td className="is-right pred-num">{formatBrier(set.stats.brier)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
